<template>
  <div id="news-article-detail" v-loading="isLoading">    
    <el-form
      ref="form"
      :model="articleForm"
      :rules="rules"
      label-position="top"
      :disabled="!isAdding && (isEditing && !checkAccessRight('edit', articleForm))"
      @submit.native.prevent="handleSubmit"
    >
      <el-row :gutter="40" class="article-content">
        <el-col :xs="24" :md="12">
          <el-form-item
            :label="$t('TITLE_PHOTO') + ` (${$t('RECOMMENDED_TITLE_PHOTO_SIZE')})`"
            prop="coverPhoto"
            class="is-required"
          >
            <image-upload
              :photoFileUpload.sync="articleForm.coverPhoto"
              isForm
              :disabled="!isAdding && (isEditing && !checkAccessRight('edit', articleForm))"
            />
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="6">
          <el-form-item :label="$t('PUBLISH_DATE')" prop="publishDate">
            <el-date-picker
              class="w-100"
              disabled
              v-model="articleForm.publishDate"
              :size="isMobile ? 'mini' : null"
              placement="bottom-start"
              prefix-icon="custom-icon icon-calendar mini"
              :format="isMobile ? EL_DATE_FORMAT_MOBILE : EL_DATE_FORMAT"
              value-format="timestamp"
              @focus="e => e.blur()"
            />
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="6">
          <el-form-item :label="$t('STATUS')" prop="status">
            <el-select
              class="w-100"
              :disabled="!canChangeStatus(articleForm.status) || isAdding ||  ['PENDING_FOR_APPROVAL', 'APPROVED', 'REJECTED'].includes(articleForm.status)"
              v-model="articleForm.status"
              :class="getStatusClass(articleForm.status)"
            >
              <el-option
                v-for="item in allSelectableStatusListFor(articleForm.status)"
                :key="item.value"
                :label="$t(item.value)"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="12">
          <el-form-item
            :label="$t('NEWS_CATEGORY')"
            prop="categoryList"
            class="is-required"            
          >
            <el-select 
              class="w-100" 
              v-model="articleCategoryIdList" 
              multiple
              filterable
              :disabled="disabledFields"
            >
              <el-option
                v-for="el in categoryDropdownList"
                :key="el.id"
                :label="el.translationList | translate"
                :value="el.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-tabs
        tab-position="top"
        class="detail-language-options"
        type="card"
        stretch
        v-model="selectedLanguage"
        :before-leave="changeLang"
      >
        <el-tab-pane v-for="el in getLangList()" :key="el.code" :name="el.code">
          <el-row type="flex" align="middle" slot="label">
            <img
              class="custom-icon icon-lang mini"
              :src="getLangFlagImage(el.code)"
            />
            <span
              v-if="!isMobile || el.code === 'hi' || el.code === 'ta'"
            >{{ el.label }}</span>
          </el-row>
        </el-tab-pane>

        <el-row :gutter="40" class="article-content">
          <!-- hide to display article name in all pages -->
          <!-- <el-col :xs="24" :md="12">
            <el-form-item
              :label="$t('ARTICLE_NAME')"
              prop="name"
              class="is-required"
            >
              <el-input
                v-model="articleName"
                type="textarea"
                resize="none"
                :rows="2"
                maxlength="200"
                show-word-limit
              >
              </el-input>
            </el-form-item>
          </el-col> -->
          <el-col :xs="24" :md="24">
            <el-form-item
              :label="$t('ARTICLE_META_TITLE', selectedLanguage)"
              prop="metaTitle"
              class="is-required"
              >
              <el-input
                :disabled="disabledFields"
                v-model="articleMetaTitle"
                maxlength="60"
                show-word-limit
              >
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="24">
            <el-form-item
              :label="$t('ARTICLE_TITLE', selectedLanguage)"
              prop="title"
              class="is-required"
              >
              <el-input
                :disabled="disabledFields"
                v-model="articleTitle"
                type="textarea"
                resize="none"
                :rows="2"
                maxlength="200"
                show-word-limit
              >
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :md="24">
            <el-form-item
              :label="$t('ARTICLE_SHORT_DESC',selectedLanguage)"
              prop="description"
              class="is-required"
            >
              <el-input
                :disabled="disabledFields"
                v-model="articleDesc"
                type="textarea"
                resize="none"
                :rows="6"
                maxlength="200"
                show-word-limit
              >
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :md="24" v-if="!isLoading">
            <el-form-item
              :label="$t('ARTICLE_CONTENT',selectedLanguage)"
              prop="body"
              class="is-required"
            >
              <html-editor 
                :key="editorId" 
                :modelValue.sync="articleBody" 
                :length.sync="textLength"        
                :insertMiddleAds="insertMiddleAds"
                :disabled="disabledFields"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-tabs>
      
      <page-footer
        v-if="articleForm"
        :isLoading="isLoading"
        :handleBackTo="exit"
        :handleArchive="showArchiveBtn ? () => handleArchive() : null"
        :handleSubmit=" showSubmitBtn ? () => {} : null"
        :submitBtnText="$t('SUBMIT')"
        :handleAction="showSubmitBtn ?  () => handlePreview() : null"
        :actionBtnText="$t('PREVIEW')"
        :isApproval="isApprover"
        :handleApproval="isApprover ? (status) => handleApproval(status) : null"
        :approvalStatus="articleForm.status"
        :handlePublish="() => showPublishArticleDialog =! showPublishArticleDialog"
        :handleDraft="showDraftBtn ? () => handleSubmit(_,_,true) : null"        
      />
    </el-form>

    <div v-if="articleForm.articleApproval" class="approval-log">
      <h4>{{ $t('APPROVAL_LOG') }}</h4>
      <el-table
        :data="articleForm.articleApproval"
        class="newsily-bo-table"
      >
        <el-table-column prop="approver" label="Approver">
          <template slot-scope="{row}">
            {{ getFullName(row.approver) }}
          </template>
        </el-table-column>
        <el-table-column prop="date" label="Date">
          <template slot-scope="{row}">
            {{ moment(row.actionDate).format(DATE_FORMAT) }} 
          </template>
        </el-table-column>
        <el-table-column prop="time" label="Time">
          <template slot-scope="{row}">
            {{ moment(row.actionDate).format(TIME_FORMAT) }} 
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="Remarks" min-width="200px" />
        <el-table-column 
          prop="actionStatus" 
          :label="$t('STATUS')"
          width="120">
          <template slot-scope="{row}">
            <span :class="getStatusClass(row.actionStatus)">{{ $t(row.actionStatus) }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <ApprovalRemarkDialog
      v-if="showApprovalRemarkDialog"
      v-model="showApprovalRemarkDialog"
      :type="approvalStatus"
      :articleId="articleForm.id || 0"    
    />
    <PublishArticleDialog
      v-model="showPublishArticleDialog"
      :handlePublish="(date) => handleSubmit(_,date)"
    />
  </div>
</template>

<script>
import { generalMixin } from "@/utils/general-mixin";
import { articleMixin } from '@/utils/_mixins/article'
import cloneDeep from "lodash/cloneDeep";
import HTMLEditor from "@/components/quill/html-editor";
import ImageUpload from "@/components/image-upload.vue";
import PageFooter from "@/components/page-footer.vue";
import ApprovalRemarkDialog from "./components/approval-remark-dialog.vue";
import PublishArticleDialog from "./components/publish-article-dialog.vue";
import { getCategoryDropdownList } from "@/api/category.js";
import {
  addArticle,
  getBOArticleById,
  updateArticle,
  archiveArticle,
} from "@/api/article.js";
import moment from "moment";
import { mapGetters } from "vuex";
import i18n from "@/locales";
import { validateCharacter } from "@/utils/helpers.js";

/**
 * Article status constants
 */
const ARTICLE_STATUS = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING_FOR_APPROVAL',
  APPROVED: 'APPROVED',
  ACTIVE: 'ACTIVE',
  REJECTED: 'REJECTED',
  INACTIVE: 'INACTIVE'
};

/**
 * Advertisement placeholder constants
 */
const ADV_PLACEHOLDER = {
  HTML: '<pre class="adv-block" id="adv-middle" data-label="Advertisement Here" spellcheck="false" contenteditable="false"><span contenteditable="false">Advertisement Here</span></pre><br><p></p>',
  ID: 'adv-middle'
};

/**
 * Default form fields for article
 * Structure for translation fields: [{name: string, language: {id: number, code: string}}]
 */
const formFields = {
  // Multilingual fields
  title: [],         // Article title in different languages
  metaTitle: [],     // SEO meta title in different languages
  description: [],   // Article short description in different languages
  body: [],          // Article main content in different languages
  
  // Standard fields
  status: null,
  publishDate: null,
  categoryList: [],
  user: {
    id: null,
  },
  coverPhoto: null,
};

// Form validation rules
const formRules = {
  title: [
    {
      required: true,
      message: i18n.t("ARTICLE_TITLE_REQUIRED"),
      trigger: "blur",
    },
  ],
  metaTitle: [
    {
      required: true,
      message: i18n.t("ARTICLE_META_TITLE_REQUIRED"),
      trigger: "blur",
    },    
  ],
  description: [
    {
      required: true,
      message: i18n.t("ARTICLE_DESC_REQUIRED"),
      trigger: "blur",
    },
  ],
  body: [
    {
      required: true,
      message: i18n.t("ARTICLE_CONTENT_REQUIRED"),
      trigger: "blur",
    },
  ],
  coverPhoto: [
    {
      required: true,
      message: i18n.t("ARTICLE_IMAGE_REQUIRED"),
      trigger: "change",
    },
  ],
  categoryList: [
    {
      required: true,
      message: i18n.t("ARTICLE_NEWS_CATEGORY_REQUIRED"),
      trigger: "change",
    }
  ]
};

/**
 * @component ArticleDetail
 * @description Manages article creation, editing, and approval workflow
 * 
 * This component handles the complete article lifecycle:
 * - Creation of new articles
 * - Editing existing articles
 * - Article approval workflow
 * - Multilingual content management
 * - Article publishing and archiving
 */
export default {
  name: "ArticleDetail",
  mixins: [generalMixin, articleMixin],
  components: {
    ImageUpload,
    "html-editor": HTMLEditor,
    PageFooter,
    PublishArticleDialog,
    ApprovalRemarkDialog
  },
  data() {    
    return {
      isLoading: false,
      id: null,
      isAdding: false,
      isEditing: false,
      articleForm: cloneDeep(formFields),
      rules: cloneDeep(formRules),
      categoryDropdownList: [],
      moment,
      selectedLanguage: this.locale || "en",
      editorId: 0,
      textLength: 0,
      insertMiddleAds: false,
      showApprovalRemarkDialog: false,
      approvalStatus: "",
      showPublishArticleDialog: false,
    };
  },
  computed: {
    // User and language utilities from Vuex store
    ...mapGetters(["user"]),
    ...mapGetters("app", ["getLangList", "getLangFlagImage"]),
    
    // Determines if the submit button should be shown
    showSubmitBtn() {
      const canAdd = this.isAdding && this.checkAccessRight('add');
      const canEdit = this.isEditing && this.checkAccessRight('edit', this.articleForm);
      return canAdd || canEdit;
    },
    
    // Determines if the archive button should be shown
    showArchiveBtn() {
      return this.checkAccessRight('archive', this.articleForm) && this.isEditing;
    },
    
    // Determines if the save as draft button should be shown
    showDraftBtn() {
      return this.isMember && this.isAdding;
    },
    
    // Determines if form fields should be disabled
    disabledFields() {      
      // Disable fields when status is ACTIVE or INACTIVE and user is member (not during creation)
      return [ARTICLE_STATUS.ACTIVE, ARTICLE_STATUS.INACTIVE].includes(this.articleForm.status) && 
             this.isMember && !this.isAdding;
    },
    // Article title in current language
    articleTitle: {
      get() {
        const locale = this.selectedLanguage;
        return this.getNameFromTransList(this.articleForm.title, "", locale);
      },
      set(newValue) {
        this.formatLangObj("title", newValue);        
      },
    },
    
    // Article meta title (SEO) in current language
    articleMetaTitle: {
      get() {
        const locale = this.selectedLanguage;
        return this.getNameFromTransList(this.articleForm.metaTitle, "", locale);
      },
      set(newValue) {
        this.formatLangObj("metaTitle", newValue);
      },
    },
    
    // Article description in current language
    articleDesc: {
      get() {
        const locale = this.selectedLanguage;
        return this.getNameFromTransList(
          this.articleForm.description,
          "",
          locale
        );
      },
      set(newValue) {
        this.formatLangObj("description", newValue);
      },
    },
    
    // Article body content in current language
    articleBody: {
      get() {
        const locale = this.selectedLanguage;
        return this.getNameFromTransList(
          this.articleForm.body,
          "",
          locale
        );
      },
      set(newValue) {
        this.formatLangObj("body", newValue);
      },
    },
    
    // Article categories as list of IDs
    articleCategoryIdList: {
      get() {
        return this.articleForm.categoryList.map((el) => el.id); 
      },
      set(newValue) {
        this.articleForm.categoryList = this.categoryDropdownList.filter(el => newValue.includes(el.id));
      }
    }

  },
  watch: {
    selectedLanguage(val) {
      if(val){
        this.$refs["form"].validate()        
      }
    }
  },
  methods: {
    // Initiates the article approval/rejection process
    handleApproval(status) {      
      this.showApprovalRemarkDialog = true;
      this.approvalStatus = status;
    },
    
    // Handles the article preview functionality
    handlePreview() {
      const postData = this.formValidation();
      if(postData) {
        // Store article data for preview
        this.$store.dispatch('article/previewArticle', postData);
        
        // Navigate to preview page in a new tab
        this.$nextTick(() => {
          this.goTo(`/0?locale=${this.selectedLanguage}`, true);
        });
      }
    },
    
    // Validates the form and prepares data for submission
    formValidation() {
      let verifiedData = null;      
      this.$refs["form"].validate(valid => {
        // Skip validation for disabled fields to allow partial submissions
        if (!valid && !this.disabledFields) {
          verifiedData = null;
        } else {          
          const postData = cloneDeep(this.articleForm);
          
          // Rename coverPhoto to fileUpload for API compatibility
          postData.fileUpload = postData.coverPhoto;
          delete postData.coverPhoto;
          
          verifiedData = postData;
        }
      });
      return verifiedData;
    },
    // Manages advertisement placeholders in article body content
    iniPlaceholder(body) {
      const result = body.filter(article => {
        // If article already has the advertisement placeholder, return it as is
        if (article.name.includes(ADV_PLACEHOLDER.ID)) {
          return article;
        } else {
          // If insertMiddleAds is enabled, add the placeholder to content
          if (this.insertMiddleAds) {
            article.name = article.name + ADV_PLACEHOLDER.HTML;
          }
          return article;
        }
      });

      return result;
    },
    
    // Handles language tab changes in the editor
    changeLang(_, oldV) {
      // Increment editor ID to force re-render of the editor
      this.editorId++;
      
      // If current language content is empty, remove it from the body array
      if (this.textLength === 0) {
        // Filter out empty content for the previous language
        const filteredBody = this.articleForm.body.filter(el => {
          return el.language.code !== oldV;
        });
        this.articleForm.body = filteredBody;
      } 
    },
    // Validates the article title field
    validateTitle(_, __, callback) {
      if (this.articleTitle.trim().length <= 0) {
        callback(new Error(this.$t("ARTICLE_TITLE_REQUIRED")));
      } else {
        callback();
      }
    },
    
    // Validates the article description field
    validateDesc(_, __, callback) {
      if (this.articleDesc.trim().length <= 0) {
        callback(new Error(this.$t("ARTICLE_DESC_REQUIRED")));
      } else {
        callback();
      }
    },
    
    // Validates the article meta title field
    validateMetaTitle(_, __, callback) {
      // Check if meta title is empty but title exists
      if ((this.articleMetaTitle === "" && this.articleTitle !== '') || 
          this.articleMetaTitle.trim().length <= 0) {
        callback(new Error(this.$t("ARTICLE_META_TITLE_REQUIRED")));
      } 
      // Check for invalid characters in meta title
      else if (validateCharacter(this.articleMetaTitle)) {
        callback(new Error(this.$t("ARTICLE_META_INVALID_FORMAT")));
      } 
      else {
        callback();
      }
    },
    
    // Validates the article content field
    validateContent(_, __, callback) {
      // Check if content is empty (only one language with no content)
      if (this.textLength <= 0 && this.articleForm.body.length == 1) {
        callback(new Error(this.$t("ARTICLE_CONTENT_REQUIRED")));
      } else {
        callback();
      }
    },
    // Manages multilingual content for a specific field
    formatLangObj(field, value) {
      const locale = this.selectedLanguage;
      
      // Find existing content for the current language
      let index = this.articleForm[field].findIndex(
        (el) => el.language.code === locale
      );      
      
      if (value) {
        // If value is provided, update or create content
        let obj = this.articleForm[field][index];
        
        if (obj == null) {
          // Create new language entry if it doesn't exist
          obj = {
            name: value,
            language: this.getLangList().find((el) => el.code === locale),
          };
          index = this.articleForm[field].length;
        } else {
          // Update existing language entry
          obj.name = value;
        }
        
        // Use Vue's reactive update method
        this.$set(this.articleForm[field], index, obj);
      } else {
        // If value is empty, filter out empty content for current language
        // while preserving existing content with IDs (for editing)
        this.articleForm[field] = this.articleForm[field].filter(el => {
          return (el.id != null) || (el.language.code !== locale);
        });
      }
    },
    // Navigates back to the previous page
    exit() {
      this.$router.go(-1);
    },
    
    // Fetches the list of available categories from the API
    async getCategory() {
      try {
        if (this.id) this.isLoading = true;
        const res = await getCategoryDropdownList();
        if (res.status) {
          this.categoryDropdownList = cloneDeep(res.result);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        if (this.id) this.isLoading = false;
      }
    },
    // Initializes the component based on route parameters
    async mountedFn() {
      // Load categories for dropdown
      await this.getCategory();
      
      if (this.id) {
        // Edit mode - fetch article details
        this.isEditing = true;
        this.getDetail();
      } else if (this.checkAccessRight('add')) {
        // Add mode - initialize with default values
        this.isAdding = true;
        this.articleForm.publishDate = moment().valueOf();
        this.articleForm.status = ARTICLE_STATUS.ACTIVE;

        // Set default status to DRAFT for members
        if (this.isMember) {
          this.articleForm.status = ARTICLE_STATUS.DRAFT;
        }
      }
    },
    // Archives the current article
    handleArchive() {      
      this.isLoading = true;
      
      archiveArticle(this.id)
        .then((res) => {
          if (res.status === "ok") {
            this.$notify({
              type: "success",
              message: this.$t("UPDATE_SUCCESSFULLY"),
              duration: this.NOTIFY_DURATION,
              position: this.NOTIFY_POSITION
            });
            this.exit();
          }
        })
        .catch(this.handleError)
        .finally(() => {
          this.isLoading = false;
        });
    },
    // Handles article submission for both new and existing articles
    async handleSubmit(_, publishDate = null, isDraft = false) {
      // Validate form and prepare data
      const postData = this.formValidation();
      
      if (!postData) {
        return; // Validation failed
      }
      
      // Process article body content and add advertisement placeholders
      postData.body = this.iniPlaceholder(postData.body);
      
      // Convert category objects to ID list for API
      postData.categoryIdList = postData.categoryList.map(el => el.id);
      delete postData.categoryList; // Remove original category objects

      // Special handling for member users
      if (this.isMember) {
        delete postData.publishDate; // Members can't set publish date
        
        if (isDraft) {
          // Save as draft
          postData.status = ARTICLE_STATUS.DRAFT;
        } else if (this.isAdding) {
          // New article submission goes to approval workflow
          postData.status = ARTICLE_STATUS.PENDING;
        } else if (this.isEditing) {
          // Only change status if not already published
          if (![ARTICLE_STATUS.ACTIVE, ARTICLE_STATUS.INACTIVE].includes(postData.status)) {
            postData.status = ARTICLE_STATUS.PENDING;
          }
        }
      }

      // Special handling for approvers publishing articles
      if (publishDate && this.isApprover) {
        postData.publishDate = publishDate;
        postData.status = ARTICLE_STATUS.ACTIVE;
      }
      
      // Validate meta titles across languages
      if (this.metaTitleValidation(postData)) {   
        // Submit to appropriate API endpoint
        if (this.isAdding) {
          this.handleAdd(postData);
        } else {
          this.handleEdit(postData);
        }
      } else {          
        // Trigger validation when meta title is missing
        return this.$refs["form"].validate();
      }        
    },
    // Validates that all languages with article titles also have meta titles
    metaTitleValidation(data) {
      // Check if any language has a title but no meta title
      if (data.metaTitle.length !== data.title.length && data.title.length > 0) {
        // Find languages that have titles but no meta titles
        const title = data.title;
        const metaTitle = data.metaTitle;
        
        const missingMetaTitles = title.filter(titleItem => {
          // Find titles that don't have a corresponding meta title
          return !metaTitle.some(metaTitleItem => {
            return titleItem.language.id === metaTitleItem.language.id;
          });
        });
        
        // Switch to the first language with missing meta title
        if (missingMetaTitles.length > 0) {
          this.selectedLanguage = missingMetaTitles[0].language.code;
          return false;
        }
      }
      
      return true;
    },
    // Handles adding a new article
    handleAdd(postData) {
      this.isLoading = true;
      
      addArticle(postData)
        .then((res) => {
          if (res.status === "ok") {
            // Show success notification
            this.$notify({
              type: "success",
              message: this.$t("ADDED_SUCCESSFULLY"),
              duration: this.NOTIFY_DURATION,
              position: this.NOTIFY_POSITION
            });
            
            // Show coin reward notification if applicable
            if (res.result?.coins && res.result?.transactionType === "IN") {
              this.$notify({
                title: this.$t("GAINED_COIN_TITLE"),
                message: this.$t("GAINED_COIN_MSG", { amount: res.result.coins }),
                type: 'success',
                duration: this.NOTIFY_DURATION,
                position: this.NOTIFY_POSITION,
                dangerouslyUseHTMLString: true
              });
            }
            
            // Return to article list
            this.exit();
          }
        })
        .catch(this.handleError)
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    // Handles updating an existing article
    handleEdit(postData) {      
      this.isLoading = true;
      
      updateArticle(postData)
        .then((res) => {
          if (res.status === "ok") {
            // Show success notification
            this.$notify({
              type: "success",
              message: this.$t("UPDATE_SUCCESSFULLY"),
              duration: this.NOTIFY_DURATION,
              position: this.NOTIFY_POSITION
            });
            
            // Return to article list
            this.exit();
          }
        })
        .catch(this.handleError)
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    // Fetches article details from the API
    getDetail() {
      this.isLoading = true;
      
      const requestData = {
        id: this.id,
        getWhenCategoryInactive: true, // Include inactive categories
      };
      
      getBOArticleById(requestData)
        .then((res) => {
          if (res.status === "ok") {
            // Update form with article data
            this.articleForm = cloneDeep(res.result);
          }
        })
        .catch(this.handleError)
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  // Component initialization
  mounted() {
    // Get article ID from route params
    this.id = this.$route.params.id;
    
    // Set current user as article owner
    this.articleForm.user.id = this.user.id;
    
    // Set up custom validators
    const contentValidator = { validator: this.validateContent, trigger: 'blur' };
    const metaTitleValidator = { validator: this.validateMetaTitle, trigger: 'blur' };
    const titleValidator = { validator: this.validateTitle, trigger: 'blur' };
    const descValidator = { validator: this.validateDesc, trigger: 'blur' };
    
    // Add custom validators to form rules
    this.rules.body.push(contentValidator);
    this.rules.metaTitle.push(metaTitleValidator);
    this.rules.title.push(titleValidator);
    this.rules.description.push(descValidator);
    
    // Initialize component based on mode (add/edit)
    this.mountedFn();
  },
  
  // Clean up before leaving the route
  async beforeRouteLeave(_, __, next) {
    // Clear any article preview data from store
    this.$store.dispatch('article/previewArticle', null);
    next();
  },
};
</script>

<style lang="scss">
@import "@/assets/style/_variables.scss";
@import "@/assets/style/mixin/general.scss";
@import "@/assets/style/mixin/detail.scss";
@import "@/assets/style/mixin/table.scss";

#news-article-detail {
  @include detail-language-options;
  @include detail-form;
  .approval-log {
    padding: 12px 15px;
    h4 {
      margin-bottom: 1rem;
    }
    @include newsily-bo-table;
  }

  background-color: $white;
  border-radius: 10px;
  padding-bottom: 2rem;
  padding-top: 1rem;

  .article-content {
    padding: 0 0.8rem;
    margin-bottom: 1rem;
  }

  .id-block {
    background: #f3f5fa 0% 0% no-repeat padding-box;
    border-radius: 15px;
    opacity: 1;
    letter-spacing: 0px;
    color: #666c79;
    padding: 10px;
    font-size: 12px;

    b {
      color: #253858;
    }
  }

  .break-line {
    border-top: 1px dashed #e8e8e8;
    margin-bottom: 1rem;
  }

  .image-upload.form .change-delete-text-wrapper,.image-upload.form .upload-text {
    right: unset;
    left: 16px;
  }

  .icon-lang {
    margin-right: 5px;
  }
}

.mobile {
  #news-article-detail {
    @include detail-language-options-mobile;

    .image-upload.form .change-delete-text-wrapper, .image-upload.form .upload-text {
      right: 0px!important;
      width: 100%;
      left: 0px!important;
    }

  }
}
</style>
